import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtKwp } from "./layoutMath";

const box = "ios-no-zoom bg-[#18181B] border-zinc-800 text-zinc-100 max-w-lg w-[calc(100%-1rem)] sm:w-full rounded-xl p-4 sm:p-6";

export function LayoutAdjustDialog({ open, onClose, refInfo, count, saving, onSave }) {
  const kwpEach = Number(refInfo?.comp?.kwp_or_kw) || 0;
  const valid = kwpEach > 0 && kwpEach < 2;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={box} data-testid="roof-layout-adjust-dialog">
        <DialogHeader className="text-left pr-6">
          <DialogTitle className="font-display text-lg font-bold inline-flex items-center gap-2">
            <RefreshCw size={17} className="text-amber-400" /> Angebot an die Belegung anpassen?
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-[13px]">{refInfo?.label}</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg bg-[#0F0F11] border border-zinc-800 p-3 font-mono text-[13px]" data-testid="roof-layout-adjust-summary">
          {refInfo?.modules} → <b className="text-[#00E575]">{count} Module</b>
          {valid && <span className="text-zinc-400"> · ca. {fmtKwp(refInfo.modules * kwpEach)} → <b className="text-zinc-100">{fmtKwp(count * kwpEach)} kWp</b></span>}
        </div>
        <p className="text-[12.5px] text-zinc-300 leading-relaxed">
          {refInfo?.kind === "offer"
            ? "Es entsteht ein neues Angebot mit neuer Nummer und neuem Preis. Das bisherige Angebot wird als abgelöst markiert und sein Signatur-Link gesperrt."
            : "Die Konfiguration wird aktualisiert, Preis und Ertrag werden neu berechnet. Teile pro Modul (z. B. Montage) werden mitgezählt."}
        </p>
        <DialogFooter className="gap-2 sm:gap-2">
          <button type="button" onClick={() => onSave(false)} disabled={saving} data-testid="roof-layout-save-only"
            className="h-10 px-4 rounded-lg border border-zinc-700 text-zinc-200 text-[13px] font-semibold hover:bg-zinc-800">
            Nur Belegung speichern
          </button>
          <button type="button" onClick={() => onSave(true)} disabled={saving} data-testid="roof-layout-save-adjust"
            className="h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-900 text-[13px] font-extrabold inline-flex items-center justify-center gap-1.5">
            {saving && <Loader2 size={14} className="animate-spin" />} Speichern &amp; anpassen
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LayoutPreviewDialog({ open, onClose, projectId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    let u;
    api.get(`/projects/${projectId}/pv-layout/preview.jpg`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); })
      .catch(() => setUrl(""));
    return () => { if (u) URL.revokeObjectURL(u); setUrl(null); };
  }, [open, projectId]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${box} sm:max-w-3xl`} data-testid="roof-layout-preview-dialog">
        <DialogHeader className="text-left pr-6">
          <DialogTitle className="font-display text-lg font-bold">Belegungsplan im PDF</DialogTitle>
          <DialogDescription className="text-zinc-400 text-[13px]">So sieht der Plan auf der Seite „Ihr Belegungsplan“ aus.</DialogDescription>
        </DialogHeader>
        {url === null && <div className="h-48 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>}
        {url === "" && <p className="text-rose-300 text-[13px]">Vorschau konnte nicht erzeugt werden.</p>}
        {url && <img src={url} alt="Belegungsplan" className="w-full rounded-lg border border-zinc-800" data-testid="roof-layout-preview-image" />}
      </DialogContent>
    </Dialog>
  );
}
